import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  Cloud,
  Download,
  FileCheck2,
  FileSpreadsheet,
  ShieldAlert,
} from "lucide-react";
import { useState, type FormEvent } from "react";
import { api, download, errorMessage } from "../api";
import { EmptyState, LoadingBlock, Message } from "../components/Feedback";
import { dateTimeFormatter } from "../format";

type ExportRecord = {
  id: string;
  number: string;
  type: string;
  periodFrom: string;
  periodTo: string;
  fileName: string;
  sha256: string;
  status: string;
  oneCDocumentNumbers: string[];
  createdByName: string;
  createdAt: string;
};

type SheetsStatus = {
  enabled: boolean;
  configured: boolean;
  mode: string;
  containsPersonalData: boolean;
};

export function AccountingPage() {
  const queryClient = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = `${today.slice(0, 8)}01`;
  const [periodFrom, setPeriodFrom] = useState(monthStart);
  const [periodTo, setPeriodTo] = useState(today);
  const [message, setMessage] = useState<{
    tone: "error" | "success" | "info";
    text: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const exportsQuery = useQuery({
    queryKey: ["accounting-exports"],
    queryFn: () =>
      api<{ exports: ExportRecord[] }>("/api/accounting/exports"),
  });
  const sheetsQuery = useQuery({
    queryKey: ["google-sheets-status"],
    queryFn: () =>
      api<SheetsStatus>("/api/integrations/google-sheets/status"),
  });

  async function createExport(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      await download("/api/accounting/exports/stock", { periodFrom, periodTo });
      setMessage({
        tone: "success",
        text: "Файл создан и зарегистрирован. Передайте его бухгалтеру через утверждённый канал.",
      });
      await queryClient.invalidateQueries({ queryKey: ["accounting-exports"] });
    } catch (caught) {
      setMessage({ tone: "error", text: errorMessage(caught) });
    } finally {
      setBusy(false);
    }
  }

  async function sendToSheets() {
    setBusy(true);
    setMessage(null);
    try {
      const result = await api<{ rowsProcessed: number }>(
        "/api/integrations/google-sheets/stock-summary",
        { method: "POST" },
      );
      setMessage({
        tone: "success",
        text: `В Google Sheets отправлено агрегированных строк: ${result.rowsProcessed}.`,
      });
    } catch (caught) {
      setMessage({ tone: "error", text: errorMessage(caught) });
    } finally {
      setBusy(false);
    }
  }

  async function confirmInOneC(record: ExportRecord) {
    const raw = window.prompt(
      "Укажите номера документов 1С через запятую. Подтверждение попадёт в аудит:",
      record.oneCDocumentNumbers.join(", "),
    );
    if (!raw) return;
    const documentNumbers = raw
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
    try {
      await api(`/api/accounting/exports/${record.id}/confirm-1c`, {
        method: "POST",
        body: JSON.stringify({ documentNumbers }),
      });
      await queryClient.invalidateQueries({ queryKey: ["accounting-exports"] });
    } catch (caught) {
      setMessage({ tone: "error", text: errorMessage(caught) });
    }
  }

  if (exportsQuery.isLoading) {
    return <LoadingBlock label="Загружаем журнал выгрузок…" />;
  }

  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <p className="eyebrow">Параллельный учёт</p>
          <h2>Файлы для ручного отражения в 1С</h2>
          <p>
            CRM формирует рабочий Excel-файл и контрольную сумму. Бухгалтер
            вручную проводит документы в 1С и подтверждает их номера.
          </p>
        </div>
      </section>

      <section className="accounting-grid">
        <article className="panel export-builder">
          <header className="panel__header">
            <div>
              <p className="eyebrow">Excel</p>
              <h2>Выгрузка складских движений</h2>
            </div>
            <FileSpreadsheet size={24} className="text-green" />
          </header>
          <form className="form-grid" onSubmit={(event) => void createExport(event)}>
            <label>
              Период с
              <input type="date" value={periodFrom} onChange={(event) => setPeriodFrom(event.target.value)} required />
            </label>
            <label>
              Период по
              <input type="date" value={periodTo} onChange={(event) => setPeriodTo(event.target.value)} required />
            </label>
            <button className="button button--primary span-2" type="submit" disabled={busy}>
              <Download size={18} /> {busy ? "Готовим файл…" : "Создать и скачать Excel"}
            </button>
          </form>
          <div className="legal-note">
            <ShieldAlert size={19} />
            <p>
              Файл не является автоматически проведённым документом 1С или
              подписанным электронным оригиналом.
            </p>
          </div>
        </article>

        <article className="panel">
          <header className="panel__header">
            <div>
              <p className="eyebrow">Google Sheets</p>
              <h2>Агрегированный отчёт</h2>
            </div>
            <Cloud size={24} className="text-blue" />
          </header>
          <div className="integration-status">
            <span
              className={`status-dot ${
                sheetsQuery.data?.enabled && sheetsQuery.data.configured
                  ? "status-dot--ok"
                  : "status-dot--warn"
              }`}
            >
              {sheetsQuery.data?.enabled && sheetsQuery.data.configured
                ? "Готов к отправке"
                : "Отключён до согласования"}
            </span>
            <p>
              Передаются только SKU, товар, склад и агрегированные количества.
              Имена, телефоны и данные пользователей исключены.
            </p>
            <button
              className="button button--secondary"
              type="button"
              onClick={() => void sendToSheets()}
              disabled={
                busy ||
                !sheetsQuery.data?.enabled ||
                !sheetsQuery.data.configured
              }
            >
              <Cloud size={17} /> Отправить текущую сводку
            </button>
          </div>
        </article>
      </section>

      {message ? <Message tone={message.tone}>{message.text}</Message> : null}

      <section className="panel">
        <header className="panel__header">
          <div>
            <p className="eyebrow">Контроль передачи</p>
            <h2>Реестр бухгалтерских файлов</h2>
          </div>
          <span className="panel__meta">Последние 100</span>
        </header>
        {exportsQuery.data?.exports.length ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Выгрузка</th>
                  <th>Период</th>
                  <th>Создал(а)</th>
                  <th>SHA-256</th>
                  <th>Статус</th>
                  <th aria-label="Действие" />
                </tr>
              </thead>
              <tbody>
                {exportsQuery.data.exports.map((record) => (
                  <tr key={record.id}>
                    <td>
                      <strong>{record.number}</strong>
                      <span className="table-subline">{record.fileName}</span>
                    </td>
                    <td>
                      {record.periodFrom} — {record.periodTo}
                    </td>
                    <td>
                      {record.createdByName}
                      <span className="table-subline">
                        {dateTimeFormatter.format(new Date(record.createdAt))}
                      </span>
                    </td>
                    <td>
                      <code className="hash-code" title={record.sha256}>
                        {record.sha256.slice(0, 12)}…
                      </code>
                    </td>
                    <td>
                      <span
                        className={`status-pill ${
                          record.status === "POSTED_IN_1C"
                            ? "status-pill--accepted"
                            : "status-pill--pending_approval"
                        }`}
                      >
                        {record.status === "POSTED_IN_1C"
                          ? "Проведено в 1С"
                          : "Файл создан"}
                      </span>
                      {record.oneCDocumentNumbers.length ? (
                        <span className="table-subline">
                          {record.oneCDocumentNumbers.join(", ")}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      {record.status !== "POSTED_IN_1C" ? (
                        <button
                          className="button button--secondary button--small"
                          type="button"
                          onClick={() => void confirmInOneC(record)}
                        >
                          <FileCheck2 size={16} /> Подтвердить 1С
                        </button>
                      ) : (
                        <CheckCircle2 size={20} className="text-green" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            title="Выгрузок ещё нет"
            description="Первый созданный Excel-файл появится в этом реестре вместе с SHA-256."
          />
        )}
      </section>
    </div>
  );
}

